import { useEffect, useRef, useState } from "react";
import SlotCounter from "react-slot-counter";

type TimerProps = {
  days: number[];
  hours: number[];
  minutes: number[];
  seconds: number[];
};

let intervalId: NodeJS.Timeout;
const CountdownTimer = ({
  EndDateTime,
  textClass,
  numericClass,
  containerClass,
  format,
}: {
  EndDateTime: Date;
  textClass: string;
  numericClass: string;
  containerClass?: string;
  format: string;
}) => {
  const [currentTime, setCurrentTime] = useState<TimerProps>({
    days: [0, 0],
    hours: [0, 0],
    minutes: [0, 0],
    seconds: [0, 0],
  });
  const remainingDays = useRef<number>(0);
  const remainingHours = useRef<number>(0);
  const remainingMinutes = useRef<number>(0);
  const remainingSeconds = useRef<number>(0);

  useEffect(() => {
    if (EndDateTime) calculateRemaingTime();
    return () => {
      clearInterval(intervalId);
    };
  }, [EndDateTime]);

  const [d, h, m, s] = format.split(":");

  const isBST = function () {
    const now = new Date();
    const year = now.getFullYear();
    const start = new Date(year, 2, 31); // Last Sunday in March
    const end = new Date(year, 9, 31); // Last Sunday in October
    let day = start.getDay(); // Day of the week of the last Sunday in March
    start.setDate(31 - day); // Set start date to last Sunday in March
    day = end.getDay(); // Day of the week of the last Sunday in October
    end.setDate(31 - day); // Set end date to last Sunday in October
    return now >= start && now < end;
  };

  const calculateDateNowUk = function () {
    const now = new Date(); // Get current date and time

    // Get current time zone offset in minutes
    const local_offset = now.getTimezoneOffset();

    // Convert local time to UTC time
    const utc_time = now.getTime() + local_offset * 60000;

    // Define UK time zone offset in minutes
    let uk_offset = 0; // Offset for GMT (Greenwich Mean Time)
    if (isBST()) {
      uk_offset += 60; // Add 1 hour for BST (British Summer Time)
    }

    // Convert UTC time to UK time
    const uk_time = utc_time + uk_offset * 60000;

    // Create a new Date object representing UK time
    const uk_date_time = new Date(uk_time);

    return uk_date_time;
  };

  const calculateRemaingTime = () => {
    const now = calculateDateNowUk();
    const date_future = EndDateTime;

    // get total seconds between the times
    var delta = (date_future.valueOf() - now.valueOf()) / 1000;

    // only display numbers if end date is in the future
    if (delta > 0) {
      // calculate (and subtract) whole days
      const days_left = Math.floor(delta / 86400);
      delta -= days_left * 86400;
      remainingDays.current = days_left;

      // calculate (and subtract) whole hours
      const hours_left = Math.floor(delta / 3600) % 24;
      delta -= hours_left * 3600;
      remainingHours.current = hours_left;

      // calculate (and subtract) whole minutes
      const minutes_left = Math.floor(delta / 60) % 60;
      delta -= minutes_left * 60;
      remainingMinutes.current = minutes_left;

      // what's left is seconds
      remainingSeconds.current = Math.floor(delta);

      updateCountdown();
      startCountdownClock();
    }
  };

  const getFormattedDate = () => {
    const dayFirstPart = remainingDays.current < 10 ? 0 : Math.floor(remainingDays.current / 10);
    const daySecondPart = remainingDays.current < 10 ? remainingDays.current : remainingDays.current % 10;
    const hoursFirstPart = remainingHours.current < 10 ? 0 : Math.floor(remainingHours.current / 10);
    const hourseSecondPart = remainingHours.current < 10 ? remainingHours.current : remainingHours.current % 10;
    const minutesFirstPart = remainingMinutes.current < 10 ? 0 : Math.floor(remainingMinutes.current / 10);
    const minutesSecondPart = remainingMinutes.current < 10 ? remainingMinutes.current : remainingMinutes.current % 10;
    const secondsFirstPart = remainingSeconds.current < 10 ? 0 : Math.floor(remainingSeconds.current / 10);
    const secondsSecondPart = remainingSeconds.current < 10 ? remainingSeconds.current : remainingSeconds.current % 10;
    return {
      dayFirstPart,
      daySecondPart,
      hoursFirstPart,
      hourseSecondPart,
      minutesFirstPart,
      minutesSecondPart,
      secondsFirstPart,
      secondsSecondPart,
    };
  };

  const updateCountdown = () => {
    const {
      dayFirstPart,
      daySecondPart,
      hoursFirstPart,
      hourseSecondPart,
      minutesFirstPart,
      minutesSecondPart,
      secondsFirstPart,
      secondsSecondPart,
    } = getFormattedDate();
    setCurrentTime({
      days: [dayFirstPart, daySecondPart],
      hours: [hoursFirstPart, hourseSecondPart],
      minutes: [minutesFirstPart, minutesSecondPart],
      seconds: [secondsFirstPart, secondsSecondPart],
    });
  };

  const startCountdownClock = () => {
    intervalId = setInterval(() => {
      remainingSeconds.current--;
      if (
        remainingSeconds.current < 0 &&
        remainingMinutes.current === 0 &&
        remainingHours.current === 0 &&
        remainingDays.current === 0
      ) {
        clearInterval(intervalId);
        return;
      }
      if (remainingSeconds.current < 0) {
        remainingSeconds.current = 59;
        remainingMinutes.current--;
      }
      if (remainingMinutes.current < 0) {
        remainingMinutes.current = 59;
        remainingHours.current--;
      }
      if (remainingHours.current < 0) {
        remainingHours.current = 23;
        remainingDays.current--;
      }
      updateCountdown();
    }, 1000);
  };

  return (
    <div className={`${containerClass}`}>
      <span className={`${textClass}`}>Ends in</span>
      <SlotCounter
        value={`${currentTime.days[0]}${currentTime.days[1]}`}
        startValue="00"
        sequentialAnimationMode={true}
        direction="top-down"
        charClassName={`${numericClass}`}
        startValueOnce={true}
      />
      <span className={`${numericClass}`}>{d}</span>
      <span className={`${numericClass}`}>:</span>
      <SlotCounter
        value={`${currentTime.hours[0]}${currentTime.hours[1]}`}
        startValue="00"
        sequentialAnimationMode={true}
        direction="top-down"
        charClassName={`${numericClass}`}
        startValueOnce={true}
      />
      <span className={`${numericClass}`}>{h}</span>
      <span className={`${numericClass}`}>:</span>
      <SlotCounter
        value={`${currentTime.minutes[0]}${currentTime.minutes[1]}`}
        startValue="00"
        sequentialAnimationMode={true}
        direction="top-down"
        charClassName={`${numericClass}`}
        startValueOnce={true}
      />
      <span className={`${numericClass}`}>{m}</span>
      <span className={`${numericClass}`}>:</span>
      <SlotCounter
        value={`${currentTime.seconds[0]}${currentTime.seconds[1]}`}
        startValue="00"
        sequentialAnimationMode={true}
        direction="top-down"
        charClassName={`${numericClass}`}
        startValueOnce={true}
      />
      <span className={`${numericClass}`}>{s}</span>
    </div>
  );
};

export default CountdownTimer;
